import React from 'react';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from './ui/dropdown-menu';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { 
  Sun, 
  Moon, 
  Palette, 
  Settings,
  Trophy,
  Sparkles,
  Code2,
  Github,
} from 'lucide-react';

const ThemeIcon = ({ theme }) => {
  switch (theme) {
    case 'midnight':
      return <Moon className="h-4 w-4" />;
    case 'colorful':
      return <Palette className="h-4 w-4" />;
    default:
      return <Sun className="h-4 w-4" />;
  }
};

export const Header = ({ 
  currentTheme, 
  themes, 
  onThemeChange,
  gamificationEnabled,
  onToggleGamification,
  suggestion,
}) => {
  return (
    <header className="h-14 border-b border-border/30 bg-card/80 backdrop-blur-sm sticky top-0 z-50">
      <div className="h-full px-4 flex items-center justify-between">
        {/* Logo & Brand */}
        <div className="flex items-center gap-3">
          <div className="relative group">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary via-accent to-secondary flex items-center justify-center shadow-glow transition-transform duration-300 group-hover:scale-105">
              <Code2 className="h-5 w-5 text-white" />
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-success rounded-full border-2 border-card animate-pulse" />
          </div>
          <div>
            <h1 className="font-bold text-lg text-foreground tracking-tight">
              PyZone
              <span className="text-primary mr-1">🐍</span>
            </h1>
            <p className="text-[10px] text-muted-foreground -mt-0.5">לומדים Python בכיף!</p>
          </div>
        </div>

        {/* Center - AI Suggestion or Quick Stats */}
        <div className="hidden md:flex items-center gap-4 max-w-md">
          {suggestion ? (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 animate-in fade-in slide-in-from-left-4 duration-500">
              <span className="text-[9px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded font-bold uppercase tracking-tight shrink-0">AI</span>
              <p className="text-xs text-foreground/80 truncate italic leading-tight">{suggestion}</p>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted/50">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-foreground">סביבת תכנות אינטראקטיבית</span>
            </div>
          )}
        </div>

        {/* Right - Controls */}
        <div className="flex items-center gap-2">
          {/* Theme Switcher */}
          <TooltipProvider>
            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="relative overflow-hidden hover:bg-muted"
                    >
                      <ThemeIcon theme={currentTheme?.id} />
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>החלף ערכת נושא</p>
                </TooltipContent>
              </Tooltip>

              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>ערכת נושא</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {Object.values(themes).map((theme) => (
                  <DropdownMenuItem
                    key={theme.id}
                    onClick={() => onThemeChange(theme.id)}
                    className={currentTheme?.id === theme.id ? 'bg-muted' : ''}
                  >
                    <span className="ml-2 text-lg">{theme.icon}</span>
                    <div className="flex flex-col">
                      <span className="font-medium">{theme.name}</span>
                      <span className="text-xs text-muted-foreground">{theme.description}</span>
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </TooltipProvider>

          {/* Settings Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="hover:bg-muted">
                <Settings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>הגדרות</DropdownMenuLabel>
              <DropdownMenuSeparator />
              
              {/* Gamification Toggle */}
              <div className="flex items-center justify-between px-2 py-2">
                <div className="flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-gamification-xp" />
                  <Label htmlFor="gamification" className="text-sm cursor-pointer">
                    משחוק (XP והישגים)
                  </Label>
                </div>
                <Switch
                  id="gamification"
                  checked={gamificationEnabled}
                  onCheckedChange={onToggleGamification}
                />
              </div>
              
              <DropdownMenuSeparator />
              
              <DropdownMenuItem asChild>
                <a 
                  href="https://github.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center"
                >
                  <Github className="h-4 w-4 ml-2" />
                  קוד מקור
                </a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};
